import { useState, useCallback, useMemo } from 'react'
import moment from 'moment'
import {
  getFiltersForRole,
  getFilterByKey,
  getFilterDropdownOptions,
  FILTER_TYPES,
} from './filterDefinitions.js'
import { formatFilterValueForDisplay } from './filterTransformers.js'
import { useMarketplaceParams } from '../../hooks/useMarketplaceParams.js'

/**
 * useMarketplaceFiltersLogic
 *
 * Hook for MarketplaceFilters component.
 * Manages filter state, selection, and persistence.
 *
 * Reads committed filters from URL via useMarketplaceParams.
 * Uses local staged state while the modal is open.
 * Commits staged filters to URL on Apply/Close.
 */
export const useMarketplaceFiltersLogic = ({
  userRole = 'admin',
  tenant = null,
  listType = 'orders',
}) => {
  const { adminFilters, setFilters, clearFilters } = useMarketplaceParams()

  // Modal state
  const [isOpen, setIsOpen] = useState(false)

  // Staged filters — local copy while modal is open
  const [stagedFilters, setStagedFilters] = useState([])

  // Current filter selection state
  const [selectedFilterKey, setSelectedFilterKey] = useState(null)
  const [selectedValue, setSelectedValue] = useState(null)

  // Loading states for Apply/Clear buttons
  const [isApplying, setIsApplying] = useState(false)
  const [isClearing, setIsClearing] = useState(false)

  // The "active" filters — staged when modal is open, URL-derived otherwise
  const appliedFilters = isOpen ? stagedFilters : adminFilters

  // Get available filters for user role
  const availableFilters = useMemo(
    () => getFiltersForRole(userRole, tenant, listType),
    [userRole, tenant, listType],
  )

  // Get dropdown options
  const filterOptions = useMemo(
    () => getFilterDropdownOptions(availableFilters),
    [availableFilters],
  )

  // Available filter options — exclude non-multi filters already applied
  const availableFilterOptions = useMemo(() => {
    return filterOptions.filter((opt) => {
      const def = getFilterByKey(opt.value)
      if (def?.multiSelect) return true
      return !appliedFilters.some((f) => f.key === opt.value)
    })
  }, [filterOptions, appliedFilters])

  // Get current filter definition
  const currentFilterDef = useMemo(
    () => (selectedFilterKey ? getFilterByKey(selectedFilterKey) : null),
    [selectedFilterKey],
  )

  // Open modal — initialize staged filters from URL state
  const handleOpen = useCallback(() => {
    setStagedFilters([...adminFilters])
    setIsOpen(true)
    if (adminFilters.length === 0) {
      setSelectedFilterKey(FILTER_TYPES.DATE)
      setSelectedValue(moment().format('l'))
    } else {
      setSelectedFilterKey(null)
      setSelectedValue(null)
    }
  }, [adminFilters])

  // Commit staged filters to URL and close modal.
  // The pending-driver-acceptance filter only makes sense in the `tendered`
  // category, so applying it navigates there (mirrors the legacy behavior).
  const commitAndClose = useCallback(() => {
    const hasPendingDriver = stagedFilters.some(
      (f) => f.key === FILTER_TYPES.PENDING_DRIVER_ACCEPTANCE,
    )
    setFilters(
      stagedFilters,
      hasPendingDriver ? { category: 'tendered' } : undefined,
    )
    setIsOpen(false)
    setSelectedFilterKey(null)
    setSelectedValue(null)
  }, [stagedFilters, setFilters])

  // Close modal — commit staged filters to URL
  const handleClose = useCallback(() => {
    commitAndClose()
  }, [commitAndClose])

  // Select filter type
  const handleFilterTypeSelect = useCallback((filterKey) => {
    setSelectedFilterKey(filterKey)
    const filterDef = getFilterByKey(filterKey)
    setSelectedValue(filterDef?.defaultValue ?? null)
  }, [])

  // Update filter value
  const handleValueChange = useCallback((value) => {
    setSelectedValue(value)
  }, [])

  // Add/update filter — supports multi-select append for IMC, createdBy
  const handleAddFilter = useCallback(() => {
    if (
      !selectedFilterKey ||
      selectedValue === null ||
      selectedValue === undefined
    ) {
      return
    }

    const filterDef = getFilterByKey(selectedFilterKey)

    setStagedFilters((prev) => {
      const existing = prev.find((f) => f.key === selectedFilterKey)

      if (existing && filterDef.multiSelect) {
        // Append: merge new values into existing using Set deduplication
        const existingValues = Array.isArray(existing.value)
          ? existing.value
          : [existing.value]
        const newValues = Array.isArray(selectedValue)
          ? selectedValue
          : [selectedValue]
        const mergedValue = [
          ...new Set([
            ...existingValues.map((v) => v.uid || v),
            ...newValues.map((v) => v.uid || v),
          ]),
        ]
        // Preserve full objects where available
        const allItems = [...existingValues, ...newValues]
        const deduped = mergedValue.map(
          (uid) => allItems.find((item) => (item.uid || item) === uid) || uid,
        )
        const merged = { ...existing, value: deduped }
        return [...prev.filter((f) => f.key !== selectedFilterKey), merged]
      }

      // Non-multi: replace existing filter
      const newFilter = {
        key: selectedFilterKey,
        name: filterDef.name,
        value: selectedValue,
      }
      return [...prev.filter((f) => f.key !== selectedFilterKey), newFilter]
    })

    // Reset selection
    setSelectedFilterKey(null)
    setSelectedValue(null)
  }, [selectedFilterKey, selectedValue])

  // Remove single filter
  const handleRemoveFilter = useCallback((filterKey) => {
    setStagedFilters((prev) => prev.filter((f) => f.key !== filterKey))
  }, [])

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setIsClearing(true)
    setStagedFilters([])
    clearFilters()
    setSelectedFilterKey(null)
    setSelectedValue(null)
    setTimeout(() => setIsClearing(false), 300)
  }, [clearFilters])

  // Apply filters and close modal
  const handleApplyFilters = useCallback(() => {
    setIsApplying(true)
    commitAndClose()
    setTimeout(() => setIsApplying(false), 300)
  }, [commitAndClose])

  // Format filter for display in chips
  const formatFilterDisplay = useCallback((filter) => {
    return {
      ...filter,
      displayValue: formatFilterValueForDisplay(filter),
    }
  }, [])

  // Get applied filters with display values
  const appliedFiltersWithDisplay = useMemo(
    () => appliedFilters.map(formatFilterDisplay),
    [appliedFilters, formatFilterDisplay],
  )

  // Determine if Add button should be disabled
  const isAddDisabled = useMemo(() => {
    if (
      !selectedFilterKey ||
      selectedValue === null ||
      selectedValue === undefined
    ) {
      return true
    }
    // For checkbox group values (objects), disable if ALL values are false
    if (
      typeof selectedValue === 'object' &&
      selectedValue !== null &&
      !Array.isArray(selectedValue)
    ) {
      const allFalse = Object.values(selectedValue).every((v) => !v)
      if (allFalse) return true
    }
    return false
  }, [selectedFilterKey, selectedValue])

  // Check if filter is already applied
  const isFilterApplied = useCallback(
    (filterKey) => appliedFilters.some((f) => f.key === filterKey),
    [appliedFilters],
  )

  return {
    // Modal state
    isOpen,
    handleOpen,
    handleClose,

    // Filter options
    availableFilters,
    filterOptions,
    availableFilterOptions,

    // Current selection
    selectedFilterKey,
    currentFilterDef,
    selectedValue,
    handleFilterTypeSelect,
    handleValueChange,

    // Applied filters
    appliedFilters,
    appliedFiltersWithDisplay,
    filterCount: adminFilters.length,

    // Actions
    handleAddFilter,
    handleRemoveFilter,
    handleClearFilters,
    handleApplyFilters,

    // Button states
    isApplying,
    isClearing,
    isAddDisabled,

    // Utilities
    isFilterApplied,

    // Context
    userRole,
    tenant,
    listType,
  }
}
